'use client'

import { Loader2, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export type ShareRoomDomainJoinSectionProps = {
  showCustomerEmailInviteUi: boolean
  companyJoinHint: string | null
  domainShareLinkLoading: boolean
  generatedLink: string | null
  onCopyJoinLink: () => void
}

export function ShareRoomDomainJoinSection({
  showCustomerEmailInviteUi,
  companyJoinHint,
  domainShareLinkLoading,
  generatedLink,
  onCopyJoinLink,
}: ShareRoomDomainJoinSectionProps) {
  return (
    <div className="shrink-0 space-y-0">
      <div className="space-y-2">
        <div className="space-y-1 px-0.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Option 1 — Share a join link
          </p>
          <p className="text-[11px] leading-snug text-slate-600">
            Copy one link for your teammates: they open it, enter their email, sign in if they already have access, or
            finish a short profile when they are new—allowed company domains can self-join using the same URL.
          </p>
        </div>
        {(domainShareLinkLoading || generatedLink) && (
          <div className="space-y-3 rounded-xl border border-emerald-200/90 bg-emerald-50/70 p-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-900">Room share link</p>
              {companyJoinHint ? (
                <p className="text-[11px] font-medium text-emerald-950/90">Allowed domains: {companyJoinHint}</p>
              ) : null}
            </div>
            {domainShareLinkLoading ? (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading share link…
              </div>
            ) : generatedLink ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <Input readOnly value={generatedLink} className="font-mono text-xs sm:min-w-0 sm:flex-1" />
                <Button type="button" variant="secondary" className="shrink-0 sm:w-auto" onClick={onCopyJoinLink}>
                  <Copy className="mr-2 h-4 w-4" aria-hidden />
                  Copy
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="mt-5 shrink-0 space-y-1.5 border-t border-slate-200 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Option 2 — Invite people to the room
        </p>
        <p className="text-[11px] leading-snug text-slate-600">
          {showCustomerEmailInviteUi
            ? 'Enter names and email addresses below to send invitations—each person is added to this room when they accept.'
            : 'Search your directory or paste emails below to send invitations and add members directly.'}
        </p>
      </div>
    </div>
  )
}
